import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { GENDER_ARRAY } from '../../../../utils/Gr8piConfig';
import { PrimeReactErrorMessage } from '../../../../components/prime-formik';
import { intlShape } from '../../../../components/prop-types';
import { RespondentType } from '../../../../shared/Constants';

const messages = defineMessages({
  cancel: {
    id: 'actions.cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 'actions.save',
    defaultMessage: 'Save',
  },
  add: {
    id: 'candidate.add',
    defaultMessage: 'Add Respondent',
  },
  gender_M: {
    id: 'attrs.gender.M',
    defaultMessage: 'Male',
  },
  gender_F: {
    id: 'attrs.gender.F',
    defaultMessage: 'Female',
  },
  gender_U: {
    id: 'attrs.gender.U',
    defaultMessage: 'Unspecified',
  },
  gender: {
    id: 'attrs.gender',
    defaultMessage: 'Gender',
  },
  respondentTypes: {
    id: 'attrs.candidateType',
    defaultMessage: 'Respondent type',
  },
  internal: { id: 'respondent.internal', defaultMessage: 'Internal respondents' },
  external: { id: 'respondent.external', defaultMessage: 'External respondents' },
  locale: {
    id: 'attrs.locale',
    defaultMessage: 'Preferred language',
  },
  required: {
    id: 'errors.validation.required',
    defaultMessage: 'Required',
  },
  invalidEmail: {
    id: 'validation-messages.field.invalid-email',
    defaultMessage: 'Invalid email',
  },
});

const buildRespondentTypes = (formatMessage) => [
  {
    value: RespondentType.INTERNAL,
    label: formatMessage(messages.internal),
  },
  {
    value: RespondentType.EXTERNAL,
    label: formatMessage(messages.external),
  },
];

const validateForm = (formValue, formatMessage) => {
  const errors = {};

  if (!formValue.firstName) {
    errors.firstName = formatMessage(messages.required);
  }
  if (!formValue.lastName) {
    errors.lastName = formatMessage(messages.required);
  }

  // eslint-disable-next-line max-len
  const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  if (!formValue.email) {
    errors.email = formatMessage(messages.required);
  } else if (!formValue.email.match(emailPattern)) {
    errors.email = formatMessage(messages.invalidEmail);
  }

  if (!formValue.gender) {
    errors.gender = formatMessage(messages.required);
  }
  if (!formValue.type) {
    errors.type = formatMessage(messages.required);
  }
  if (!formValue.locale) {
    errors.locale = formatMessage(messages.required);
  }

  return errors;
};

class AddRespondentForm extends Component {
  constructor(props) {
    super(props);
    const { initialValues } = props;
    this.state = {
      formValue: {
        ...initialValues,
      },
      errors: {},
    };

    this.changeInput = this.changeInput.bind(this);
    this.saveRespondent = this.saveRespondent.bind(this);
  }

  saveRespondent() {
    const {
      intl: { formatMessage },
      saveRespondent,
    } = this.props;
    const { formValue } = this.state;
    const validatedErrors = validateForm(formValue, formatMessage);

    if (Object.getOwnPropertyNames(validatedErrors).length !== 0) {
      this.setState({
        errors: validatedErrors,
      });
    } else {
      saveRespondent(formValue);
    }
  }

  changeInput(field, value) {
    const { formValue, errors } = this.state;

    const updatedFormValue = {
      ...formValue,
      [field]: value,
    };
    const updatedErrors = {
      ...errors,
      [field]: null,
    };
    this.setState({
      formValue: updatedFormValue,
      errors: updatedErrors,
    });
  }

  render() {
    const {
      toggleEdit,
      intl: { formatMessage },
      translatedTags,
    } = this.props;

    const cancelLabel = formatMessage(messages.cancel);
    const saveLabel = formatMessage(messages.save);
    const formHeader = formatMessage(messages.add);
    const genderLabel = formatMessage(messages.gender);
    const respondentTypeLabel = formatMessage(messages.respondentTypes);
    const localeLabel = formatMessage(messages.locale);
    const {
      formValue: { firstName, lastName, gender, type, locale, email },
      errors,
    } = this.state;

    const genderOptions = GENDER_ARRAY.map((key) => ({
      value: key,
      label: formatMessage(messages[`gender_${key}`]),
    }));

    const respondentTypes = buildRespondentTypes(formatMessage);
    return (
      <Fieldset legend={formHeader}>
        <div className="p-grid">
          <div className="p-md-6 p-sm-12">
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <FormattedMessage id="lbl.first_name" defaultMessage="First name">
                  {(text) => <label htmlFor="firstName">{text}</label>}
                </FormattedMessage>
              </div>
              <div className="p-col-12">
                <InputText
                  id="firstName"
                  value={firstName}
                  onChange={(event) => {
                    this.changeInput('firstName', event.target.value);
                  }}
                />
                {errors.firstName && <PrimeReactErrorMessage msg={errors.firstName} />}
              </div>
            </div>
          </div>
          <div className="p-md-6 p-sm-12">
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <FormattedMessage id="lbl.last_name" defaultMessage="Last name">
                  {(text) => <label htmlFor="lastName">{text}</label>}
                </FormattedMessage>
              </div>
              <div className="p-col-12">
                <InputText
                  id="lastName"
                  value={lastName}
                  onChange={(event) => {
                    this.changeInput('lastName', event.target.value);
                  }}
                />
                {errors.lastName && <PrimeReactErrorMessage msg={errors.lastName} />}
              </div>
            </div>
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <FormattedMessage id="attrs.email" defaultMessage="Email">
              {(text) => <label htmlFor="email">{text}</label>}
            </FormattedMessage>
          </div>
          <div className="p-col-12">
            <InputText
              id="email"
              value={email}
              onChange={(event) => {
                this.changeInput('email', event.target.value);
              }}
            />
            {errors.email && <PrimeReactErrorMessage msg={errors.email} />}
          </div>
        </div>
        <div className="p-grid">
          <div className="p-md-6 p-sm-12">
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <FormattedMessage id="attrs.gender" defaultMessage="Gender">
                  {(text) => <label htmlFor="gender">{text}</label>}
                </FormattedMessage>
              </div>
              <div className="p-col-12">
                <Dropdown
                  value={gender}
                  inputId="gender"
                  options={genderOptions}
                  placeholder={genderLabel}
                  onChange={(event) => {
                    this.changeInput('gender', event.value);
                  }}
                />
                {errors.gender && <PrimeReactErrorMessage msg={errors.gender} />}
              </div>
            </div>
          </div>
          <div className="p-md-6 p-sm-12">
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <FormattedMessage id="attrs.candidateType" defaultMessage="Respondent type">
                  {(text) => <label htmlFor="type">{text}</label>}
                </FormattedMessage>
              </div>
              <div className="p-col-12">
                <Dropdown
                  value={type}
                  inputId="type"
                  options={respondentTypes}
                  placeholder={respondentTypeLabel}
                  onChange={(event) => {
                    this.changeInput('type', event.value);
                  }}
                />
                {errors.type && <PrimeReactErrorMessage msg={errors.type} />}
              </div>
            </div>
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <FormattedMessage id="attrs.locale" defaultMessage="Preferred language">
              {(text) => <label htmlFor="locale">{text}</label>}
            </FormattedMessage>
          </div>
          <div className="p-md-6 p-sm-12">
            <Dropdown
              value={locale}
              inputId="locale"
              options={translatedTags}
              placeholder={localeLabel}
              onChange={(event) => {
                this.changeInput('locale', event.value);
              }}
            />
            {errors.locale && <PrimeReactErrorMessage msg={errors.locale} />}
          </div>
        </div>
        <div className="p-grid">
          <div className="p-col">
            <Button type="button" label={saveLabel} onClick={this.saveRespondent} />
            <Button
              type="button"
              onClick={() => {
                toggleEdit(false);
              }}
              className="p-button-warning"
              label={cancelLabel}
            />
          </div>
        </div>
      </Fieldset>
    );
  }
}

AddRespondentForm.propTypes = {
  intl: intlShape.isRequired,
  saveRespondent: PropTypes.func.isRequired,
  toggleEdit: PropTypes.func.isRequired,
  translatedTags: PropTypes.arrayOf(PropTypes.any).isRequired,
  initialValues: PropTypes.objectOf(PropTypes.any).isRequired,
};

const mapState = (state) => {
  const { translatedTags, locale } = state.i18n;
  const initialValues = {
    firstName: '',
    lastName: '',
    gender: 'U',
    type: RespondentType.EXTERNAL,
    locale,
  };
  return {
    translatedTags,
    initialValues,
  };
};

export default connect(mapState)(injectIntl(AddRespondentForm));
